import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { StatusBadge } from '../initiatives/StatusBadge';
import { LinkList } from '../layout/LinkList';
import { cognocracyComponents, cognocracyProjectSummary, type Initiative } from '../../data/initiatives';
import { fadeUp } from '../../utils/motion';
type ProjectFeatureProps = {
  project: Initiative;
};

/** A project presented in the site's standard heading-left, text-right section layout. */
export function ProjectFeature({
  project
}: ProjectFeatureProps) {
  return <section aria-labelledby={`project-${project.slug}`} className="bg-surface-bg py-20 lg:py-28" data-id="element-1790" data-mp="ij5hbt96szvl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1791" data-mp="wer5xzbidhnh">
        <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-1792" data-mp="scckd7ggg3si">
          <div className="lg:col-span-5" data-id="element-1793" data-mp="16bm0660r0vvy">
            <p className="text-sm font-medium text-teal mb-3" data-id="element-1794" data-mp="168hmmi1e8nsm">{project.category}</p>
            <h2 id={`project-${project.slug}`} className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-1795" data-mp="11dkeddbxuigs">
              {project.name}
            </h2>
            <StatusBadge className="mt-4" data-id="element-1796" data-mp="1hcw21mtw3y9f">{project.status}</StatusBadge>
          </div>

          <div className="lg:col-span-7" data-id="element-1797" data-mp="1le99g1cms9ig">
            <p className="text-xl text-ink-dark leading-relaxed" data-id="element-1798" data-mp="1lfajq3ltorz4">{project.description}</p>
            <p className="mt-5 text-lg text-ink-muted leading-relaxed" data-id="element-1799" data-mp="1qe3ydzcuip0v">{cognocracyProjectSummary}</p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-id="element-1800" data-mp="1vbdn1cseuwnn">
              <a href={project.href} className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal" data-id="element-1801" data-mp="202vpy6dzv7b9">
                {project.cta}
                <ArrowRight size={18} aria-hidden="true" data-id="element-1802" data-mp="dzvqp2whx6ow" />
              </a>
              <a href="/government" className="inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-1803" data-mp="9zbymdqs4t9m">
                See the NCDS government solution <ArrowRight size={16} aria-hidden="true" data-id="element-1804" data-mp="iyfgjee7n2mj" />
              </a>
            </div>
          </div>
        </motion.div>

        <div className="mt-12 lg:mt-14" data-id="element-1805" data-mp="spjwohfmgtiy">
          <h3 className="text-sm font-medium text-ink-muted mb-2" data-id="element-1806" data-mp="soi9jelc0gkv">Built on</h3>
          <LinkList label={`Components ${project.name} is built on`} items={cognocracyComponents.map(component => ({
          title: component.name,
          description: component.body,
          href: component.href
        }))} data-id="element-1807" data-mp="16pulzg1aa020" />
        </div>
      </div>
    </section>;
}